import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './client'
import type { Branch, Envelope } from './types'

export interface BranchPayload {
  name: string
  address: string
  latitude: number
  longitude: number
  geofence_radius_m: number
  timezone: string
  is_active: boolean
}

export interface QrPayload {
  version: number
  type: string
  payload: string
  message?: string
}

export function useBranches() {
  return useQuery({
    queryKey: ['branches'],
    queryFn: async (): Promise<Branch[]> => {
      const { data } = await api.get<Envelope<Branch[]>>('/admin/branches')
      return data.data
    },
    // El listado de sucursales cambia muy rara vez.
    staleTime: 5 * 60_000,
  })
}

function useBranchInvalidation() {
  const queryClient = useQueryClient()

  return () => {
    queryClient.invalidateQueries({ queryKey: ['branches'] })
    queryClient.invalidateQueries({ queryKey: ['dashboard'] })
  }
}

export function useCreateBranch() {
  const invalidate = useBranchInvalidation()

  return useMutation({
    mutationFn: async (payload: BranchPayload): Promise<Branch> => {
      const { data } = await api.post<Envelope<Branch>>('/admin/branches', payload)
      return data.data
    },
    onSuccess: invalidate,
  })
}

export function useUpdateBranch() {
  const invalidate = useBranchInvalidation()

  return useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: BranchPayload }) => {
      const { data } = await api.put<Envelope<Branch>>(`/admin/branches/${id}`, payload)
      return data.data
    },
    onSuccess: invalidate,
  })
}

export function useDeleteBranch() {
  const invalidate = useBranchInvalidation()

  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/admin/branches/${id}`)
    },
    onSuccess: invalidate,
  })
}

/**
 * Payload imprimible del QR vigente.
 *
 * No se cachea: es información sensible y cada consulta queda auditada en el
 * backend, así que se pide explícitamente cada vez que el admin la abre.
 */
export function useRevealQr() {
  return useMutation({
    mutationFn: async (id: number): Promise<QrPayload> => {
      const { data } = await api.get<QrPayload>(`/admin/branches/${id}/qr`)
      return data
    },
  })
}

export function useRotateQr() {
  const invalidate = useBranchInvalidation()

  return useMutation({
    mutationFn: async (id: number): Promise<QrPayload> => {
      const { data } = await api.post<QrPayload>(`/admin/branches/${id}/qr/rotate`)
      return data
    },
    onSuccess: invalidate,
  })
}

/** Zonas horarias frecuentes en Argentina; el backend acepta cualquier IANA. */
export const TIMEZONES = [
  'America/Argentina/Buenos_Aires',
  'America/Argentina/Cordoba',
  'America/Argentina/Mendoza',
  'America/Argentina/Salta',
  'America/Argentina/Tucuman',
  'America/Argentina/Ushuaia',
]
